import { useSeo } from '../lib/seo';
/**
 * Public /awards/vote page — the people's-choice ballot (e.g. Best Day Out),
 * separated from the long 2026 finalists shortlist on /awards so voters
 * don't have to scroll through every category to find it.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Award, Check, Clock, MapPin, Trophy, Vote } from 'lucide-react';
import api from '../lib/api';
import NewsletterSignup from '../components/NewsletterSignup';
import { track } from '../lib/tracker';
import { NominationForm } from './AwardsPage';

const AWARDS_NIGHT = '2026-11-26T19:00:00Z'; // Thursday 26 November 2026, National Space Centre

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    mins: Math.floor(diff / 60000) % 60,
    secs: Math.floor(diff / 1000) % 60,
    over: diff === 0,
  };
}

function Countdown({ target, label, sub }) {
  const t = useCountdown(target);
  if (t.over) return null;
  const units = [
    { v: t.days, l: t.days === 1 ? 'day' : 'days' },
    { v: t.hours, l: 'hrs' },
    { v: t.mins, l: 'mins' },
    { v: t.secs, l: 'secs' },
  ];
  return (
    <div className="mt-10" data-testid="awards-countdown">
      <div className="label text-mustard mb-3 inline-flex items-center gap-2"><Clock size={13}/> {label}</div>
      <div className="flex items-stretch gap-2 sm:gap-3">
        {units.map((u) => (
          <div key={u.l} className="backdrop-blur-md bg-base/10 border border-base/15 rounded-2xl px-3 py-3 sm:px-5 sm:py-4 text-center min-w-[64px] sm:min-w-[84px]" data-testid={`countdown-${u.l}`}>
            <div className="font-display text-2xl sm:text-4xl leading-none tabular-nums">{String(u.v).padStart(2, '0')}</div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-base/60 mt-1.5">{u.l}</div>
          </div>
        ))}
      </div>
      {sub && <p className="text-sm text-base/60 mt-3" data-testid="countdown-sub">{sub}</p>}
    </div>
  );
}

function getVoterToken() {
  let t = localStorage.getItem('gl_voter_token');
  if (!t) {
    t = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('gl_voter_token', t);
  }
  return t;
}

function getVotedSet() {
  try { return new Set(JSON.parse(localStorage.getItem('gl_voted_nominees') || '[]')); }
  catch { return new Set(); }
}
function addVoted(nomineeId) {
  const s = getVotedSet(); s.add(nomineeId);
  localStorage.setItem('gl_voted_nominees', JSON.stringify([...s]));
}

export default function AwardsVotePage() {
  useSeo(
    'Vote Now — Leicestershire Tourism Awards | Go Leicestershire',
    'Cast your vote in the public-choice categories of the Leicestershire Tourism Awards.'
  );
  const [cycle, setCycle] = useState(null);
  const [nomineesByCategory, setNomineesByCategory] = useState({});
  const [voted, setVoted] = useState(getVotedSet());

  useEffect(() => {
    api.get('/awards/current').then((r) => {
      setCycle(r.data || {});
      if (r.data?.id && (r.data.status === 'voting_open' || r.data.status === 'voting_closed' || r.data.status === 'published')) {
        api.get(`/awards/cycles/${r.data.id}/nominees`).then((r2) => {
          const grouped = {};
          r2.data.items.forEach((n) => {
            (grouped[n.category_id] = grouped[n.category_id] || []).push(n);
          });
          setNomineesByCategory(grouped);
        });
      }
    });
  }, []);

  async function vote(nomineeId) {
    if (voted.has(nomineeId)) return;
    try {
      await api.post(`/awards/cycles/${cycle.id}/vote`, { nominee_id: nomineeId, voter_token: getVoterToken() });
      track.awardVote(nomineeId);
      addVoted(nomineeId);
      setVoted(new Set([...voted, nomineeId]));
    } catch (e) {
      alert(e.response?.data?.detail || 'Vote failed');
    }
  }

  if (!cycle) return <div className="py-32 text-center text-muted">Loading...</div>;

  const status = cycle.status;
  const isVoting = status === 'voting_open';
  const isClosed = status === 'voting_closed' || status === 'published';
  const catNames = (cycle.categories || []).map((c) => c.name).join(' · ');

  if (!cycle.id || (!isVoting && !isClosed)) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-32 text-center" data-testid="vote-page-not-open">
        <Vote size={48} className="mx-auto text-mustard mb-6"/>
        <h1 className="font-display text-4xl sm:text-5xl mb-4">Public voting hasn't opened yet</h1>
        <p className="text-muted text-lg max-w-xl mx-auto mb-8">When the public vote opens, the entries and voting buttons will appear right here.</p>
        <Link to="/awards" className="inline-flex items-center gap-2 bg-heritage text-base px-6 py-3 rounded-full font-semibold hover:bg-heritage-deep" data-testid="vote-page-back-link">
          <ArrowLeft size={16}/> Back to the Tourism Awards
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-charcoal text-base py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40"/>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-mustard/10 rounded-full blur-3xl"/>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-5 inline-flex items-center gap-2"><Vote size={14}/> {cycle.year} · Public vote</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-5 max-w-3xl">
            {catNames ? `${catNames}: you decide.` : 'The public vote.'}
          </h1>
          <p className="text-lg md:text-xl text-base/85 max-w-2xl mb-8 leading-relaxed">
            {isVoting
              ? 'This award is chosen by you, not the judges. Browse the entries below and vote for your favourite — one vote per entry.'
              : 'Voting has now closed — thank you to everyone who took part. Here are the entries and results.'}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {isVoting && <span className="inline-flex items-center gap-2 bg-mustard text-charcoal px-5 py-3 rounded-full font-semibold"><Vote size={16}/> Voting is now open</span>}
            {isClosed && <span className="inline-flex items-center gap-2 bg-heritage text-base px-5 py-3 rounded-full font-semibold"><Award size={16}/> Voting closed</span>}
            <Link to="/awards" data-testid="vote-hero-back-to-awards"
              className="inline-flex items-center gap-2 border border-base/40 text-base px-5 py-3 rounded-full font-semibold hover:border-base transition-colors">
              <ArrowLeft size={16}/> All 2026 finalists
            </Link>
          </div>
          {isVoting && cycle.voting_closes_at && (
            <Countdown
              target={cycle.voting_closes_at}
              label="Voting closes in"
              sub="Winners revealed at the awards night — Thursday 26 November, National Space Centre, Leicester."
            />
          )}
          {isClosed && (
            <Countdown
              target={AWARDS_NIGHT}
              label="Winners revealed in"
              sub="The awards night — Thursday 26 November 2026, National Space Centre, Leicester."
            />
          )}
        </div>
      </section>

      {/* BALLOT */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {isVoting && voted.size > 0 && (
            <div className="bg-mustard/15 border border-mustard rounded-3xl p-6 md:p-8 mb-10 max-w-3xl" data-testid="awards-vote-signup">
              <div className="label text-heritage mb-2 inline-flex items-center gap-2"><Vote size={12}/> Thanks for voting</div>
              <h3 className="font-display text-xl md:text-2xl mb-2">Want to know who wins?</h3>
              <p className="text-charcoal/80 mb-4 text-sm md:text-base">
                Add your email — we&apos;ll tell you the moment results are announced. Subscribers also go into our quarterly StayPlayExplore prize draw.
              </p>
              <NewsletterSignup variant="inline" source="awards-vote" buttonLabel="Tell me who wins" successMessage="We&apos;ll be in touch on results day." />
            </div>
          )}
          {cycle.description && (
            <div className="bg-white border border-charcoal/10 rounded-3xl p-6 md:p-8 mb-10 md:mb-14 max-w-3xl" data-testid="awards-page-intro">
              <p className="text-charcoal/85 text-lg md:text-xl leading-relaxed whitespace-pre-wrap">{cycle.description}</p>
            </div>
          )}
          {(cycle.categories || []).length === 0 && (
            <div className="text-center py-16 text-muted" data-testid="awards-no-categories">Categories will be announced soon.</div>
          )}
          {(cycle.categories || []).map((c) => {
            const nominees = (nomineesByCategory[c.id] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            return (
              <div key={c.id} className="mb-16 md:mb-20" data-testid={`awards-category-${c.slug}`}>
                <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-4 border-b border-charcoal/10 pb-4">
                  <div className="max-w-2xl">
                    <div className="label text-heritage mb-2 inline-flex items-center gap-2"><Award size={12}/> Category</div>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">{c.name}</h2>
                    {c.description && <p className="text-muted mt-3 max-w-xl text-base md:text-lg">{c.description}</p>}
                  </div>
                  <div className="text-sm text-muted bg-stone px-3 py-1.5 rounded-full">{nominees.length} {nominees.length === 1 ? 'nominee' : 'nominees'}</div>
                </div>
                {nominees.length === 0 ? (
                  <div className="bg-stone/40 border border-dashed border-charcoal/15 rounded-3xl p-8 md:p-10 text-center" data-testid={`awards-category-empty-${c.slug}`}>
                    <Trophy size={32} className="mx-auto text-muted/40 mb-3"/>
                    <p className="text-muted text-base md:text-lg max-w-md mx-auto">No approved nominees in this category yet — they'll appear here as soon as the team reviews them.</p>
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {nominees.map((n) => {
                    const showTrophy = isClosed && n.is_winner;
                    const showSilver = isClosed && n.is_runner_up;
                    return (
                      <article key={n.id} id={`nominee-${n.id}`} data-testid={`nominee-${n.id}`} className={`bg-white border rounded-3xl overflow-hidden ${showTrophy ? 'border-mustard ring-2 ring-mustard/50' : 'border-charcoal/10'}`}>
                        {n.listing_live ? (
                          <Link to={`/explore/${n.attraction_slug}`} data-testid={`nominee-listing-link-${n.id}`} className="block relative aspect-[4/3] overflow-hidden group">
                            {n.hero_image ? <img src={n.hero_image} alt={n.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/> : <div className="w-full h-full bg-stone flex items-center justify-center"><Trophy size={48} className="text-muted/30"/></div>}
                            {showTrophy && <span className="absolute top-4 left-4 bg-mustard text-charcoal text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Trophy size={12}/> Winner</span>}
                            {showSilver && <span className="absolute top-4 left-4 bg-base text-charcoal text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1">Runner-up</span>}
                          </Link>
                        ) : (
                        <div className="relative aspect-[4/3] overflow-hidden">
                          {n.hero_image ? <img src={n.hero_image} alt={n.name} className="w-full h-full object-cover"/> : <div className="w-full h-full bg-stone flex items-center justify-center"><Trophy size={48} className="text-muted/30"/></div>}
                          {showTrophy && <span className="absolute top-4 left-4 bg-mustard text-charcoal text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Trophy size={12}/> Winner</span>}
                          {showSilver && <span className="absolute top-4 left-4 bg-base text-charcoal text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1">Runner-up</span>}
                        </div>
                        )}
                        <div className="p-6">
                          <h3 className="font-display text-xl leading-tight mb-1">
                            {n.listing_live ? <Link to={`/explore/${n.attraction_slug}`} className="hover:text-heritage transition-colors">{n.name}</Link> : n.name}
                          </h3>
                          {n.town && <div className="text-muted text-sm mb-3 inline-flex items-center gap-1"><MapPin size={12}/> {n.town}</div>}
                          {n.blurb && <p className="text-sm text-charcoal/85 mb-4">{n.blurb}</p>}
                          <div className={`flex items-center ${n.listing_live ? 'justify-between' : 'justify-end'}`}>
                            {n.listing_live && (
                              <Link to={`/explore/${n.attraction_slug}`} data-testid={`nominee-view-listing-${n.id}`} className="text-heritage text-xs font-semibold inline-flex items-center gap-1 hover:underline">
                                View listing <ArrowRight size={12}/>
                              </Link>
                            )}
                            {isVoting && (
                              voted.has(n.id) ? (
                                <span className="bg-forest/10 text-forest px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Check size={12}/> Voted</span>
                              ) : (
                                <button onClick={() => vote(n.id)} data-testid={`vote-${n.id}`} className="bg-heritage text-base px-4 py-2 rounded-full text-xs font-semibold hover:bg-heritage-deep">Vote</button>
                              )
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Suggest a late entry while voting is open */}
      {isVoting && (cycle.categories || []).length > 0 && (
        <section className="py-16 md:py-24 bg-stone/50 border-t border-charcoal/5" data-testid="awards-suggest-section">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <div className="label text-heritage mb-3">Missed someone?</div>
            <h2 className="font-display text-3xl md:text-4xl mb-3">Suggest a new nominee.</h2>
            <p className="text-muted text-base md:text-lg mb-10 max-w-2xl">Voting's open — but if a brilliant venue isn't yet on the ballot, tell us here. Approved late entries go straight onto the live voting list.</p>
            {cycle.categories.map((c) => (
              <NominationForm key={c.id} category={c} cycleId={cycle.id}/>
            ))}
          </div>
        </section>
      )}

      {/* Back to the shortlist */}
      <section className="py-12 border-t border-charcoal/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <Link to="/awards" data-testid="vote-footer-back-to-awards" className="inline-flex items-center gap-2 text-heritage font-semibold hover:underline">
            <ArrowLeft size={16}/> Meet all the 2026 Tourism Awards finalists
          </Link>
        </div>
      </section>
    </div>
  );
}
